/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { dateToIsoDate, IsoDatePipe, provideBuddhistDateAdapter } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlfrescoApiService, ContentNodeSelectorComponent, ContentNodeSelectorComponentData, NodeAction } from '@alfresco/adf-content-services';
import { Node, NodesApi } from '@alfresco/js-api';
import { Subject, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { JobDefinition, JobPreview, JobRule, JobsService } from './jobs.service';
import { DmsField, DmsMetadataService } from '../upload/dms-metadata.service';

/** A field the rule builder offers, with the value input it needs. */
interface FieldOption {
  field: string;
  label: string;
  /** 'text' | 'number' | 'date' | 'select' (options from the dms model) | 'none' (exists/missing only). */
  input: 'text' | 'number' | 'date' | 'select';
  dateLike?: boolean;
  numeric?: boolean;
  options?: { value: string; label: string }[];
}

/**
 * The job editor — conditions, action, schedule and notifications for one
 * scheduled job, saved as JSON under Data Dictionary/DMS Jobs and run by the
 * aca-dms-jobs sidecar. "ดูตัวอย่าง" asks the sidecar to run the condition as a
 * read-only preview so the match count is known before the job is armed.
 */
@Component({
  selector: 'app-job-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatDatepickerModule,
    IsoDatePipe,
    TranslatePipe
  ],
  // the date fields read and write Buddhist Era years
  providers: [...provideBuddhistDateAdapter()],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './job-dialog.component.html',
  styleUrl: './job-dialog.component.scss'
})
export class JobDialogComponent implements OnInit {
  private readonly jobs = inject(JobsService);
  private readonly metadata = inject(DmsMetadataService);
  private readonly translate = inject(TranslateService);
  private readonly ref = inject(MatDialogRef<JobDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly api = inject(AlfrescoApiService);

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  isEdit = false;
  saving = false;
  error = '';
  previewing = false;
  readonly preview = signal<JobPreview | null>(null);

  model: JobDefinition = JobDialogComponent.blank();

  /** dms:* fields straight from the deployed content model, with their allowed values. */
  dmsFields: FieldOption[] = [];

  private static readonly BASE_FIELDS: FieldOption[] = [
    { field: 'cm:created', label: 'DMS.JOBS.F_CREATED', input: 'date', dateLike: true },
    { field: 'cm:modified', label: 'DMS.JOBS.F_MODIFIED', input: 'date', dateLike: true },
    { field: 'cm:to', label: 'DMS.JOBS.F_EXPIRY', input: 'date', dateLike: true },
    { field: 'cm:name', label: 'DMS.JOBS.F_NAME', input: 'text' },
    { field: 'cm:title', label: 'DMS.JOBS.F_TITLE', input: 'text' },
    { field: 'cm:creator', label: 'DMS.JOBS.F_CREATOR', input: 'text' },
    { field: 'cm:owner', label: 'DMS.JOBS.F_OWNER', input: 'text' },
    { field: 'mimetype', label: 'DMS.JOBS.F_MIMETYPE', input: 'text' },
    { field: 'size', label: 'DMS.JOBS.F_SIZE', input: 'number', numeric: true },
    { field: 'TAG', label: 'DMS.JOBS.F_TAG', input: 'text' },
    { field: 'ANCESTOR', label: 'DMS.JOBS.F_ANCESTOR', input: 'text' }
  ];

  /**
   * Built-in + dms:* fields the rule builder offers. These arrays must stay
   * referentially stable between change-detection passes — a getter that
   * returned a fresh array each pass made every mat-option in the rule rows
   * destroy and recreate itself, and mat-select's reaction to that reschedules
   * change detection forever: the page freezes the moment a rule is added.
   */
  fieldOptions: FieldOption[] = [...JobDialogComponent.BASE_FIELDS];
  private fieldOptionMap = new Map<string, FieldOption>(this.fieldOptions.map((f) => [f.field, f]));
  private readonly opsCache = new Map<string, string[]>();

  readonly recipientInput = { done: '' };

  /** Schedule presets; "custom" reveals the raw cron input. */
  schedulePreset = 'daily2';
  readonly presets: { key: string; cron: string; labelKey: string }[] = [
    { key: 'hourly', cron: '0 * * * *', labelKey: 'DMS.JOBS.SCHED_HOURLY' },
    { key: 'daily0', cron: '0 0 * * *', labelKey: 'DMS.JOBS.SCHED_DAILY0' },
    { key: 'daily2', cron: '0 0 2 * * *', labelKey: 'DMS.JOBS.SCHED_DAILY2' },
    { key: 'weekly', cron: '0 0 2 * * 1', labelKey: 'DMS.JOBS.SCHED_WEEKLY' },
    { key: 'monthly', cron: '0 0 2 1 * *', labelKey: 'DMS.JOBS.SCHED_MONTHLY' },
    { key: 'custom', cron: '', labelKey: 'DMS.JOBS.SCHED_CUSTOM' }
  ];

  /** Recipients as a comma-separated string in the form; split on save. */
  notifyRecipients = '';
  actionRecipients = '';

  // 'archive' is deliberately absent — moveTo with the Archive folder as its
  // target is the same job (and a blank moveTo path falls back to it anyway).
  // Definitions saved earlier still run and still get their ACT_ARCHIVE label.
  // 'expire' carries its own condition (expiry date passed, not yet marked) —
  // rules added here only narrow it
  readonly actions = ['expire', 'moveTo', 'setExpiry', 'addTag', 'delete', 'notify', 'script'];

  private static blank(): JobDefinition {
    return {
      name: '',
      enabled: true,
      // a new job starts in rehearsal — it reports what it would touch until
      // the administrator turns the rehearsal off
      dryRun: true,
      schedule: { cron: '0 0 2 * * *', timezone: 'Asia/Bangkok' },
      condition: { nodeType: 'cm:content', rules: [] },
      action: { type: 'moveTo' },
      notify: { onComplete: false, onlyWhenMatched: true, recipients: [] }
    };
  }

  constructor() {
    const data = inject<any>(MAT_DIALOG_DATA);
    if (data?.job) {
      this.isEdit = true;
      const src = data.job as JobDefinition;
      this.model = JSON.parse(JSON.stringify(src));
      this.model.condition ??= { nodeType: 'cm:content', rules: [] };
      this.model.condition.rules ??= [];
      this.model.action ??= { type: 'archive' };
      this.model.notify ??= { onComplete: false, onlyWhenMatched: true, recipients: [] };
      this.model.schedule ??= { cron: '0 0 2 * * *', timezone: 'Asia/Bangkok' };
      this.notifyRecipients = (this.model.notify.recipients || []).join(', ');
      this.actionRecipients = (this.model.action.recipients || []).join(', ');
      // an action type no longer offered (e.g. 'archive') stays selectable so
      // editing an old definition doesn't blank the field
      if (!this.actions.includes(this.model.action.type)) {
        this.actions.unshift(this.model.action.type);
      }
      const preset = this.presets.find((p) => p.cron === this.model.schedule.cron);
      this.schedulePreset = preset?.key ?? 'custom';
      this.resolveFolderNames();
    }
  }

  /** Folder node id → "path/to/name", filled in the background so the raw id is never the only thing shown. */
  readonly folderNames = new Map<string, string>();

  ngOnInit(): void {
    // the dms:* fields come from the deployed model so custom document types
    // surface here the moment they exist — the same source the upload form reads
    this.metadata.schema().subscribe((schema) => {
      this.dmsFields = Object.values(schema.fields).map((f: DmsField) => ({
        field: f.id,
        label: JobDialogComponent.dmsLabel(f.id),
        input: f.options?.length ? 'select' : f.dataType === 'd:datetime' || f.dataType === 'd:date' ? 'date' : 'text',
        dateLike: f.dataType === 'd:datetime' || f.dataType === 'd:date',
        options: f.options
      }));
      this.fieldOptions = [...JobDialogComponent.BASE_FIELDS, ...this.dmsFields];
      this.fieldOptionMap = new Map(this.fieldOptions.map((f) => [f.field, f]));
      this.opsCache.clear();
    });
  }

  private static dmsLabel(id: string): string {
    const key = 'DMS.JOBS.F_' + id.replace('dms:', '').toUpperCase();
    return key;
  }

  fieldOption(field: string): FieldOption | undefined {
    return this.fieldOptionMap.get(field);
  }

  /** Which operators make sense for the field on a rule row — one array per field so the operator select's options stay stable. */
  opsFor(rule: JobRule): string[] {
    let ops = this.opsCache.get(rule.field);
    if (!ops) {
      const opt = this.fieldOption(rule.field);
      if (opt?.dateLike) {
        ops = ['olderThanDays', 'newerThanDays', 'before', 'after', 'between', 'exists', 'missing'];
      } else if (opt?.numeric) {
        ops = ['greaterThan', 'lessThan', 'between', 'exists', 'missing'];
      } else if (rule.field === 'ANCESTOR' || rule.field === 'TAG' || opt?.input === 'select') {
        ops = ['equals', 'notEquals', 'inList'];
      } else {
        ops = ['equals', 'notEquals', 'contains', 'inList', 'exists', 'missing'];
      }
      this.opsCache.set(rule.field, ops);
    }
    return ops;
  }

  opLabel(op: string): string {
    return this.translate.instant('DMS.JOBS.OP_' + op.toUpperCase());
  }

  /** A rule needs no value at all for exists/missing. */
  needsValue(rule: JobRule): boolean {
    return !['exists', 'missing'].includes(rule.op);
  }

  /** The rule compares against a calendar date, so its value gets a date picker. */
  isDateValue(rule: JobRule): boolean {
    return this.fieldOption(rule.field)?.input === 'date' && ['before', 'after', 'between'].includes(rule.op);
  }

  toIsoDate(date: Date | null): string {
    return dateToIsoDate(date);
  }

  addRule(): void {
    this.model.condition.rules.push({ field: 'cm:created', op: 'olderThanDays', value: '365' });
    this.preview.set(null);
  }

  removeRule(index: number): void {
    this.model.condition.rules.splice(index, 1);
    this.preview.set(null);
  }

  onRuleChange(rule: JobRule): void {
    // keep the operator legal for the field it just switched to
    if (!this.opsFor(rule).includes(rule.op)) {
      rule.op = this.opsFor(rule)[0];
    }
    this.preview.set(null);
  }

  onPresetChange(): void {
    const preset = this.presets.find((p) => p.key === this.schedulePreset);
    if (preset?.cron) {
      this.model.schedule.cron = preset.cron;
    }
  }

  // ---- folder picking ------------------------------------------------------
  // The ANCESTOR rule and the moveTo target both point at a repository folder;
  // nobody knows a node id by heart, so both get the standard node picker and
  // the picked folder's id/path is written back into the field.

  /** Opens the folder picker for a rule's value. "in list" appends, the rest replace. */
  browseFolder(rule: JobRule): void {
    this.pickFolder().subscribe((nodes) => {
      const folder = nodes?.[0];
      if (!folder) {
        return;
      }
      const existing = (rule.value ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (rule.op === 'inList') {
        if (!existing.includes(folder.id)) {
          rule.value = [...existing, folder.id].join(',');
        }
      } else {
        rule.value = folder.id;
      }
      this.preview.set(null);
      this.resolveFolderNames();
    });
  }

  /** Opens the folder picker for the moveTo action's target path. */
  browseActionPath(): void {
    this.pickFolder().subscribe((nodes) => {
      const folder = nodes?.[0];
      if (folder) {
        this.model.action.path = JobDialogComponent.nodePath(folder);
        this.preview.set(null);
      }
    });
  }

  /** Friendly "path/to/name" for a stored id list, while the ids stay the real value. */
  folderLabel(value?: string): string {
    return (value ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => this.folderNames.get(id) ?? id)
      .join(', ');
  }

  private pickFolder(): Subject<Node[]> {
    const select = new Subject<Node[]>();
    const data: ContentNodeSelectorComponentData = {
      title: 'DMS.JOBS.BROWSE_FOLDER',
      actionName: NodeAction.CHOOSE,
      currentFolderId: '-root-',
      selectionMode: 'single',
      where: '(isFolder=true)',
      isSelectionValid: (node: Node) => node.isFolder,
      select
    };
    this.dialog.open(ContentNodeSelectorComponent, { data, panelClass: 'adf-content-node-selector-dialog', width: '630px' });
    return select;
  }

  /** Fills `folderNames` for every ANCESTOR id the form currently holds. */
  resolveFolderNames(): void {
    const ids = new Set<string>();
    for (const rule of this.model.condition?.rules ?? []) {
      if (rule.field === 'ANCESTOR') {
        for (const id of (rule.value ?? '').split(',').map((s) => s.trim())) {
          if (id && !this.folderNames.has(id)) {
            ids.add(id);
          }
        }
      }
    }
    for (const id of ids) {
      from(this.nodes.getNode(id, { include: ['path'] } as any) as Promise<any>)
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          // unresolvable ids get themselves as the label so they aren't refetched per keystroke
          this.folderNames.set(id, res?.entry ? JobDialogComponent.nodePath(res.entry) : id);
        });
    }
  }

  /** "Sites/x/documentLibrary/folder" — Company Home itself is dropped from the crumbs. */
  private static nodePath(node: Node): string {
    const crumbs = (node.path?.elements ?? []).map((e) => e.name).filter((name) => name !== 'Company Home');
    return [...crumbs, node.name].join('/');
  }

  /** Split the recipients text into the stored list form. */
  private static recipientList(text: string): string[] {
    return text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  valid(): boolean {
    return !!this.model.name.trim() && !!this.model.schedule.cron.trim() && !!this.model.action.type;
  }

  /** Ask the sidecar what this job would touch, without touching it. */
  runPreview(): void {
    this.previewing = true;
    this.error = '';
    const def = { ...this.toDefinition() };
    delete def.id;
    this.jobs.preview(def).subscribe({
      next: (result) => {
        this.previewing = false;
        this.preview.set(result);
      },
      error: (e) => {
        this.previewing = false;
        this.error = e?.error?.message || this.translate.instant('DMS.JOBS.PREVIEW_FAIL');
      }
    });
  }

  private toDefinition(): JobDefinition {
    this.model.notify.recipients = JobDialogComponent.recipientList(this.notifyRecipients);
    this.model.action.recipients = JobDialogComponent.recipientList(this.actionRecipients);
    return this.model;
  }

  save(): void {
    this.saving = true;
    this.error = '';
    this.jobs.save(this.toDefinition()).subscribe({
      next: () => this.ref.close(true),
      error: (e) => {
        this.saving = false;
        this.error = e?.response?.body?.error?.briefSummary || e?.message || this.translate.instant('DMS.JOBS.SAVE_FAIL');
      }
    });
  }

  close(): void {
    this.ref.close(false);
  }
}
